import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "Product", href: "#how-it-works" },
    { label: "Builder", href: "/builder" },
    { label: "Surveyor", href: "/surveyor" },
    { label: "Workflow", href: "#workflow" },
    { label: "Screens", href: "#screens" },
    { label: "Why easypermit", href: "#different" },
  ];

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(href);
    }
  };

  return (
    <nav
      data-testid="landing-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-200/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
              <Shield className="w-[18px] h-[18px] text-white" strokeWidth={2} />
            </div>
            <span className="font-heading font-semibold text-lg text-slate-900 tracking-tight">easypermit</span>
          </Link>
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button key={link.label} onClick={() => handleNavClick(link.href)} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-md hover:bg-slate-100/60 transition-all">
                {link.label}
              </button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900" data-testid="nav-login-btn">Log in</Button>
            <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800 shadow-sm" onClick={() => handleNavClick("#role-selector")} data-testid="nav-explore-btn">Explore demo</Button>
          </div>
          <button className="md:hidden p-2 text-slate-600" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <button key={link.label} onClick={() => handleNavClick(link.href)} className="block w-full text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md">{link.label}</button>
            ))}
            <div className="pt-3 border-t border-slate-100 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1">Log in</Button>
              <Button size="sm" className="flex-1 bg-slate-900 text-white" onClick={() => handleNavClick("#role-selector")}>Explore</Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
